import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AdminPanelSettingsOutlined,
  Check,
  ExpandLess,
  ExpandMore,
  PersonOutline,
} from '@mui/icons-material'
import { Box, Collapse, ListItemText, MenuItem } from '@mui/material'

import { useEnterSystemAdminMode } from 'shared/hooks'
import { SystemAdminModeDenial } from 'shared/types'
import { useIsSystemAdminMode, useViewerMode, ViewerMode } from 'shared/utils'
import {
  IconWrapper,
  ListIconWrapper,
  NestedMenuItem,
} from './ViewerModeMenuItem.style'
import { GetCurrentUserQuery } from 'shared/data/queries.generated'

interface ViewerModeMenuItemProps {
  parentMenuOpen: boolean
  dataUser?: GetCurrentUserQuery
  onSystemAdminModeDenied: (denial: SystemAdminModeDenial) => void
}

const ViewerModeMenuItem = ({
  parentMenuOpen,
  dataUser,
  onSystemAdminModeDenied,
}: ViewerModeMenuItemProps) => {
  const { t } = useTranslation()
  const enterSystemAdminMode = useEnterSystemAdminMode(onSystemAdminModeDenied)
  const { mode, setMode } = useViewerMode()
  const isSystemAdminMode = useIsSystemAdminMode()

  const viewerCanUseSystemAdminMode =
    dataUser?.viewer?.viewerCanUseSystemAdminMode

  const [expanded, setExpanded] = useState(false)
  const [isCheckingAccess, setIsCheckingAccess] = useState(false)

  useEffect(() => {
    if (!parentMenuOpen) {
      setExpanded(false)
    }
  }, [parentMenuOpen])

  if (!viewerCanUseSystemAdminMode) {
    return null
  }

  const handleModeSelect = async (selectedMode: ViewerMode) => {
    if (selectedMode === mode) {
      return
    }

    if (selectedMode === ViewerMode.SYSTEM_ADMIN) {
      setIsCheckingAccess(true)
      await enterSystemAdminMode().finally(() => setIsCheckingAccess(false))
      return
    }

    setMode(selectedMode)
    window.location.reload()
  }

  return (
    <>
      <MenuItem
        id="viewer-mode-menu-item"
        disableRipple
        onClick={() => setExpanded(current => !current)}
        aria-haspopup="true"
        aria-controls={expanded ? 'viewer-mode-options' : undefined}
        aria-expanded={expanded}
        sx={{ gap: '4px' }}
      >
        <IconWrapper>
          {isSystemAdminMode ? (
            <AdminPanelSettingsOutlined fontSize="small" />
          ) : (
            <PersonOutline fontSize="small" />
          )}
        </IconWrapper>

        <ListItemText primary={t('viewerMode.mode')} />

        {expanded ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </MenuItem>

      <Collapse
        in={expanded}
        timeout={200}
        unmountOnExit
        sx={{ width: '100%' }}
      >
        <Box
          id="viewer-mode-options"
          role="group"
          aria-labelledby="viewer-mode-menu-item"
          sx={{
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <NestedMenuItem
            selected={mode === ViewerMode.USER}
            onClick={() => handleModeSelect(ViewerMode.USER)}
          >
            <ListIconWrapper>
              <PersonOutline fontSize="small" />
            </ListIconWrapper>

            <ListItemText primary={t('viewerMode.user')} />

            {mode === ViewerMode.USER && <Check fontSize="small" />}
          </NestedMenuItem>

          <NestedMenuItem
            selected={isSystemAdminMode}
            disabled={isCheckingAccess}
            onClick={() => handleModeSelect(ViewerMode.SYSTEM_ADMIN)}
          >
            <ListIconWrapper>
              <AdminPanelSettingsOutlined fontSize="small" />
            </ListIconWrapper>

            <ListItemText primary={t('viewerMode.systemAdmin')} />

            {isSystemAdminMode && <Check fontSize="small" />}
          </NestedMenuItem>
        </Box>
      </Collapse>
    </>
  )
}

export default ViewerModeMenuItem
